import { AlgorithmStep, CaseOptions, ErrorKey, EventStatus, ReadEventLog } from "@/store/clientApi";
import { LocalizedError } from "@/utils/helpers";

interface EventLogErrorKeys {
  translationErrorKey?: string;
  errorKey?: ErrorKey;
}

export interface APIError {
  status: number;
  defaultMessage: string;
  errorKey?: string;
}

export const InsufficientCreditsAPIError: APIError = {
  status: 409,
  defaultMessage: "insufficient credits remaining"
};

export const IsAPIError = (apiErr: APIError, localizedErr: LocalizedError) =>
  apiErr.status === localizedErr.status &&
  apiErr.defaultMessage === localizedErr.default_message &&
  (apiErr.errorKey === undefined || apiErr.errorKey === localizedErr.error_key);

export const GetEventLogErrorKeys = (
  eventLogs: ReadEventLog[],
  errorType: "error" | "warning",
  translationType: "status" | "message"
): EventLogErrorKeys => {
  const defaultReturn: EventLogErrorKeys =
    errorType === "error"
      ? {
          translationErrorKey: `issues.modelBasing.basingError.${translationType}`,
          errorKey: "model_basing_basing_error"
        }
      : {};
  const status: EventStatus = errorType === "error" ? "failed" : "warning";
  const eventLog = eventLogs.find((eventLog) => eventLog.status === status && eventLog.error_key !== undefined);
  if (eventLog === undefined || eventLog?.status === "passed" || eventLog.error_key === undefined) {
    return defaultReturn;
  }

  let errorKey = eventLog.error_key as string;
  const modelBasingPrefix = "model_basing_";
  if (!errorKey.startsWith(modelBasingPrefix)) {
    return defaultReturn;
  }

  errorKey = errorKey.slice(modelBasingPrefix.length);
  // Convert to camel case
  errorKey = errorKey.toLowerCase().replace(/([-_][a-z])/g, (group) => group.toUpperCase().replace("-", "").replace("_", ""));

  return { translationErrorKey: `issues.modelBasing.${errorKey}.${translationType}`, errorKey: eventLog.error_key };
};

export const FixableEventLogErrorInfo: {
  [id in AlgorithmStep]?: {
    options: (options: CaseOptions) => CaseOptions;
    translationKey: string;
  };
} = {
  model_basing_algorithm_step_model_hollowing: {
    translationKey: "issues.modelBasing.retry.asSolid",
    options: (options: CaseOptions) => ({ ...options, base_params: { ...options.base_params, hollow: false } })
  },
  model_basing_algorithm_step_vent_generation: {
    translationKey: "issues.modelBasing.retry.asSolid",
    options: (options: CaseOptions) => ({ ...options, base_params: { ...options.base_params, hollow: false } })
  },
  model_basing_algorithm_step_pillar_generation: {
    translationKey: "issues.modelBasing.retry.withoutAttachments",
    options: (options: CaseOptions) => ({ ...options, attachment_params: { attachment_type: null } })
  }
};
